import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';


const Chapitre4Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Générer automatiquement de nouveaux tuyaux et supprimer ceux qui sortent de l'écran.
        </p>
      </InfoBox>

      <Separator type="text" text="Génération automatique" color="gray-darker" size="md" />
      <SectionTitle>🚀 1/ Ajout de nouveaux tuyaux</SectionTitle>

      <p>On va maintenant ajouter de nouvelles paires de tuyaux, toutes les 2 secondes par exemple.<br/>
      Cette valeur sera configurable, donc on commence par créer une variable globale :</p>

      <CodeBlock language="javascript">
{`let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes`}
      </CodeBlock>

      <p>On va aussi créer une deuxième variable globale qui servira à compter le temps écoulé :</p>

      <CodeBlock language="javascript">
{`let tuyauTemps = 0;`}
      </CodeBlock>

      <p>À chaque frame, on décrémente cette variable du temps écoulé depuis la dernière frame, c'est à dire <InlineCode>dt</InlineCode> en secondes :</p>

      <CodeBlock language="javascript">
{`tuyauTemps -= dt;`}
      </CodeBlock>

      <p>Quand elle arrive à 0, on remet <InlineCode>tuyauTemps = tuyauIntervalle</InlineCode> (= 2 secondes), et on ajoute un nouveau tuyau à notre liste :</p>

      <CodeBlock language="javascript">
{`if (tuyauTemps <= 0) {
  tuyauTemps = tuyauIntervalle;
  // ajouter un nouveau tuyau à la liste
}`}
      </CodeBlock>

      <Note type="tip" title={<>💡 Pourquoi <InlineCode>tuyauTemps</InlineCode> commence à 0 ?</>}>
        <p>Cette astuce permet d'ajouter un tuyau dès le lancement du programme.<br/>
        Si on mettait <InlineCode>tuyauTemps = 2</InlineCode>, il faudrait attendre 2 secondes avant de voir un nouveau tuyau.</p>
      </Note>

      <Separator type="text" text="Création des tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🏗️ 2/ Création d'un nouveau tuyau</SectionTitle>

      <p>Il faut définir la position en x et y du nouveau tuyau. On va créer deux variables locales positionX et positionY :</p>

      <p>1️⃣ Notre nouveau tuyau doit commencer tout à droite de l'écran :</p>
      <CodeBlock language="javascript">
{`let positionX = width;`}
      </CodeBlock>
      <p>Rappel : <InlineCode>width</InlineCode> correspond à la largeur du canvas.</p>

      <p>2️⃣ Sa position verticale doit être aléatoire afin de créer de la difficulté. <InlineCode>y</InlineCode> doit être compris entre 100 et 400 afin de toujours voir le tuyau du haut et du bas :</p>
      <p>On utilise la fonction <InlineCode>random(min, max)</InlineCode> de p5.js :</p>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>random(min, max)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Génère un nombre aléatoire. <br/>- Paramètre 1 : valeur minimale<br/> - Paramètre 2 : valeur maximale<br/> - Retourne : un nombre entre min et max</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>
      <CodeBlock language="javascript">
{`let positionY = random(100, 400);`}
      </CodeBlock>

      <p>Enfin, comme dans <InlineCode>setup()</InlineCode>, on utilise la fonction <InlineCode>push()</InlineCode> pour ajouter le tuyau à la liste :</p>

      <CodeBlock language="javascript">
{`tuyaux.push({ x:  positionX, y: positionY, franchi: false });`}
      </CodeBlock>

      <p>3️⃣ On peut désormais enlever les deux tuyaux créés à la main dans <InlineCode>setup()</InlineCode>, ils ne servent plus à rien.</p>

      <Note type="exercice" title="🧪 À toi de jouer : Ajout de tuyaux toutes les 2 secondes">
        <p>Crée deux variables globales au début du code :</p>
        <CodeBlock language="javascript">
{`let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes
let tuyauTemps = 0;`}
        </CodeBlock>

        <p>A la fin de <InlineCode>draw()</InlineCode>, décrémente <InlineCode>tuyauTemps</InlineCode> :</p>
        <CodeBlock language="javascript">
{`tuyauTemps = tuyauTemps - dt;`}
        </CodeBlock>

        <p>&nbsp;&nbsp;Si <InlineCode>tuyauTemps &lt;= 0</InlineCode> :</p>
        &nbsp;&nbsp;• remets <InlineCode>tuyauTemps = tuyauIntervalle</InlineCode><br/>
        &nbsp;&nbsp;• crée <InlineCode>positionX = width</InlineCode><br/>
        &nbsp;&nbsp;• crée <InlineCode>positionY = random(100, 400)</InlineCode><br/>
        &nbsp;&nbsp;• ajoute à la liste :

        <CodeBlock language="javascript">
{`tuyaux.push({ x: positionX, y: positionY, franchi: false });`}
        </CodeBlock>
        Enleve les 2 tuyaux créés à la main dans <InlineCode>setup()</InlineCode>

                 <br/><br/>
         <Result>De nouveaux tuyaux apparaissent automatiquement toutes les 2 secondes !</Result>
       

       <CorrectionBox >
         <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes

let tuyauTemps = 0;
let tuyaux = [];

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
  // Plus besoin de créer des tuyaux à la main ici !
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
         </CodeBlock>
       </CorrectionBox>

       </Note>

      <Separator type="text" text="Suppression des tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🗑️ 3/ Suppression des tuyaux qui sortent de l'écran</SectionTitle>

      <p>Quand une paire de tuyaux dépasse le bord gauche, il faut les supprimer de la liste, sinon la liste va devenir énorme pour rien.</p>

      <p>Pour savoir si un tuyau est complètement sorti de l'écran, on parcourt chaque élément de notre liste de tuyaux et on vérifie :</p>

      <CodeBlock language="javascript">
{`if (t.x + imageTuyauHaut.width < 0) {
  tuyaux.splice(i, 1);
}`}
      </CodeBlock>

      <Note type="tip" title="📍 Explication">
        <p>Cette condition vérifie que tout le tuyau est complètement sorti de l'écran (côté gauche), y compris sa largeur. En effet, <InlineCode>t.x</InlineCode> représente le bord gauche du tuyau.</p>
        
        <p><strong>Donc :</strong></p>
        <ul className="list-disc list-inside mb-4">
          <li>Si tu fais juste <InlineCode>if (t.x &lt; 0)</InlineCode>, tu supprimerais le tuyau dès que son bord gauche commence à sortir de l'écran, alors qu'une grande partie de l'image serait encore visible.</li>
          <li>En revanche, <InlineCode>if (t.x + imageTuyauHaut.width &lt; 0)</InlineCode> attend que le bord droit du tuyau soit entièrement passé à gauche de l'écran. Il ne sera supprimé que quand il ne sera plus visible du tout.</li>
        </ul>
      </Note>

      <p>Ensuite, on utilise la fonction <InlineCode>splice()</InlineCode> pour supprimer un élément de la liste :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>liste.splice(index, 1)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Supprime 1 élément à l'index donné de la liste.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="warning" title="⚠️ Pourquoi on parcourt la liste à l'envers ?">
        <p>On parcourt à l'envers pour éviter les erreurs de décalage quand on supprime des éléments du tableau.</p>
        <br/>
        <p><strong>🔁 Si on parcourt à l'endroit :</strong><br/>
        Quand on supprime un élément, tous les éléments suivants décalent d'une position vers la gauche. Cela fait sauter un élément dans la boucle.</p>
        <CodeBlock language="javascript">
{`let tuyaux = [A, B, C, D];
i = 1 -> on supprime B → tuyaux devient [A, C, D]
i devient 2 → on regarde D, mais C est ignoré !`}
        </CodeBlock>

        <p><strong>✅ Si on parcourt à l'envers :</strong><br/>
        Les indices suivants ne bougent pas quand on supprime un élément.</p>
        <CodeBlock language="javascript">
{`let tuyaux = [A, B, C, D];
i = 3 → D est supprimé
i = 2 → C est bien vérifié ensuite`}
        </CodeBlock>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : supprimer les tuyaux qui sortent">
        <p>Dans la boucle <InlineCode>for</InlineCode> qui parcourt les tuyaux, ajoute cette condition à la fin :</p>
        <CodeBlock language="javascript">
{`for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

//………

  // Supprime s'il est complètement à gauche
  if (t.x + imageTuyauHaut.width < 0) {
    tuyaux.splice(i, 1);
  }`}
        </CodeBlock>

        <br/>
        <Result>Les tuyaux disparaissent quand ils sortent complètement de l'écran !</Result>
      </Note>

      <Separator type="text" text="Exploration" color="gray-darker" size="md" />
      <SectionTitle>🔍 Pour aller plus loin</SectionTitle>

      <p>Teste différentes valeurs pour <InlineCode>tuyauIntervalle</InlineCode> pour modifier la fréquence d'apparition des tuyaux :</p>
      <ul className="list-disc list-inside mb-4">
        <li><InlineCode>tuyauIntervalle = 1</InlineCode> (plus difficile)</li>
        <li><InlineCode>tuyauIntervalle = 3</InlineCode> (plus facile)</li>
        <li><InlineCode>tuyauIntervalle = 0.5</InlineCode> (très difficile !)</li>
      </ul>


      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Ajouter et supprimer automatiquement de nouveaux tuyaux</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;

let tuyauEspace = 130; // Espace entre les tuyaux
let tuyauVitesse = 150; // Vitesse de déplacement des tuyaux
let tuyauIntervalle = 2; // Intervalle entre les tuyaux, en secondes

let tuyauTemps = 0;
let tuyaux = [];

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);
  let dt = deltaTime / 1000;

  // Parcours la liste de tuyaux
  for (let i = tuyaux.length - 1; i >= 0; i--) {
    let t = tuyaux[i];

    // Déplace et affiche le tuyau
    t.x = t.x - tuyauVitesse * dt;
    afficherTuyaux(t);

    // Supprime s'il est complètement à gauche
    if (t.x + imageTuyauHaut.width < 0) {
      tuyaux.splice(i, 1);
    }
  }

  // Créer un nouveau tuyau toutes les x secondes
  tuyauTemps = tuyauTemps - dt;
  if (tuyauTemps <= 0) {
    tuyauTemps = tuyauIntervalle;
    let positionX = width;
    let positionY = random(100, 400);
    tuyaux.push({ x: positionX, y: positionY, franchi: false });
  }
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  // Rien ici pour l'instant
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre4Content; 